/**
 * Per-agent org-scoped host credentials (ADR-0042 pt3b, ENG-7607 / ENG-8992).
 *
 * The security-critical decision of the spawn path, pulled out of the (large,
 * side-effectful) manager-worker so it can be unit-tested in isolation:
 *
 *  1. `resolvePerAgentApiKey` - decide whether THIS agent gets its own minted,
 *     org-scoped key, or keeps the shared host-wide one. This module owns the
 *     DECISION only; delivery is spawnSession's. It deliberately lets a
 *     mint failure PROPAGATE so the caller fails the spawn CLOSED, never falling
 *     back to the shared credential.
 * There used to be a second export here, `injectPerAgentApiKey`, which wrote the
 * minted key into the spawn env object. It was REMOVED, not refactored: that
 * object is the tmux CLIENT env, and tmux inherits the starting client's env as
 * its SERVER GLOBAL — so writing a per-agent credential there could make one
 * agent's key the host-wide default for every session lacking an `-e` override.
 * Delivery belongs in `spawnSession`'s session-scoped `-e` push and nowhere else.
 * Do not reintroduce an env-object writer here.
 *
 * Why this matters: the manager forwards ONE host credential into EVERY agent
 * container. Two distinct attacks follow from that, and they are not the same
 * attack at two severities:
 *
 *  - POOL hosts (cross-org): a credential reachable from tenant A's container
 *    can mint tenant B's exchange JWT — the ENG-7433 attack-2 that no container
 *    boundary closes. Per-agent scope is MANDATORY there (ENG-7489).
 *  - DEDICATED hosts (same-org, cross-agent): agent A can name agent B in the
 *    body of `POST /host/agent-integrations` and receive B's server-decrypted
 *    integration credentials (ENG-8992). Same org, so it is not a tenancy
 *    breach — but it is still a credential-isolation breach between agents that
 *    are supposed to hold different secrets.
 */

/**
 * Decide THIS agent's host credential at spawn time.
 *
 * - **Pool host** (`hostKind === 'pool'`): always mints. Per-agent scope is
 *   mandatory there (ENG-7489 refuses a claim-less token outright), so this is
 *   deliberately NOT flag-gated — a flag that could turn it off would be a flag
 *   that can break a pool host's ability to authenticate at all.
 * - **Dedicated host**: mints only when `dedicatedEnabled` is true (the
 *   `per-agent-host-credentials` flag, resolved host-side by the manager).
 *   False — the shipped default — returns `null` and the shared host key is
 *   forwarded unchanged, byte-for-byte legacy behaviour.
 *
 * The `mint` fn throws on any non-2xx (see `mintAgentKey`); this function does
 * NOT catch — it lets the error propagate so the caller's spawn path fails
 * closed. Silently returning the shared key on a mint failure would reopen the
 * exact exposure this closes, so it is never done here.
 *
 * Note the asymmetry between the two failure directions, which is intentional:
 * a flag that fails to READ leaves behaviour unchanged (safe), but a mint that
 * fails once armed refuses the spawn (also safe). Neither degrades to "spawn
 * with the shared key".
 */
export async function resolvePerAgentApiKey(opts: {
  hostKind: string | null | undefined;
  rawHostKey: string;
  agentId: string;
  /**
   * Whether per-agent credentials are armed for this DEDICATED host. Ignored on
   * a pool host, where minting is mandatory regardless. Defaults to `false` so
   * an unthreaded caller cannot accidentally arm the fleet.
   */
  dedicatedEnabled?: boolean;
  mint: (rawHostKey: string, agentId: string) => Promise<string>;
}): Promise<string | null> {
  const isPool = opts.hostKind === 'pool';
  if (!isPool && !opts.dedicatedEnabled) return null;
  return await opts.mint(opts.rawHostKey, opts.agentId);
}
